// Lightweight per-page SEO helper. No extra deps.
// Sets document.title, meta description, canonical, OG tags, and (optional) JSON-LD.
// Cleans up on unmount so SPA navigation doesn't leave stale tags behind.
import { useEffect } from "react";

const SITE_ORIGIN = "https://5starhelpme.com";

function upsertMeta(selector, attr, attrValue, content) {
    let el = document.head.querySelector(selector);
    let created = false;
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, attrValue);
        document.head.appendChild(el);
        created = true;
    }
    const previous = el.getAttribute("content");
    el.setAttribute("content", content);
    return { el, created, previous };
}

function upsertCanonical(href) {
    let el = document.head.querySelector('link[rel="canonical"]');
    let created = false;
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
        created = true;
    }
    const previous = el.getAttribute("href");
    el.setAttribute("href", href);
    return { el, created, previous };
}

/**
 * useSeo({ title, description, path, jsonLd, jsonLdKey })
 *   title:       <title> for the page (required)
 *   description: meta description + og:description + twitter:description (required)
 *   path:        absolute pathname for canonical + og:url (e.g. "/decks")
 *   jsonLd:      array or single object of schema.org JSON-LD to inject (optional)
 *   jsonLdKey:   unique id for the JSON-LD <script> so we can clean it up (required if jsonLd is set)
 */
export function useSeo({ title, description, path, jsonLd, jsonLdKey }) {
    useEffect(() => {
        const previousTitle = document.title;
        const url = path ? `${SITE_ORIGIN}${path}` : SITE_ORIGIN + window.location.pathname;

        if (title) document.title = title;

        const desc = upsertMeta('meta[name="description"]', "name", "description", description || "");
        const ogTitle = upsertMeta('meta[property="og:title"]', "property", "og:title", title || "");
        const ogDesc = upsertMeta('meta[property="og:description"]', "property", "og:description", description || "");
        const ogUrl = upsertMeta('meta[property="og:url"]', "property", "og:url", url);
        const ogType = upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
        const twCard = upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
        const twTitle = upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title || "");
        const twDesc = upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description || "");
        const canon = upsertCanonical(url);

        // Inject JSON-LD
        const ldScripts = [];
        if (jsonLd && jsonLdKey) {
            const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
            items.forEach((obj, i) => {
                const id = `ld-${jsonLdKey}-${i}`;
                let s = document.head.querySelector(`#${id}`);
                if (!s) {
                    s = document.createElement("script");
                    s.type = "application/ld+json";
                    s.id = id;
                    document.head.appendChild(s);
                }
                s.textContent = JSON.stringify(obj);
                ldScripts.push(s);
            });
        }

        return () => {
            // Restore previous values so the homepage default stays sane
            document.title = previousTitle;
            if (desc.created) desc.el.remove(); else if (desc.previous != null) desc.el.setAttribute("content", desc.previous);
            if (ogTitle.created) ogTitle.el.remove(); else if (ogTitle.previous != null) ogTitle.el.setAttribute("content", ogTitle.previous);
            if (ogDesc.created) ogDesc.el.remove(); else if (ogDesc.previous != null) ogDesc.el.setAttribute("content", ogDesc.previous);
            if (ogUrl.created) ogUrl.el.remove(); else if (ogUrl.previous != null) ogUrl.el.setAttribute("content", ogUrl.previous);
            if (ogType.created) ogType.el.remove(); else if (ogType.previous != null) ogType.el.setAttribute("content", ogType.previous);
            if (twCard.created) twCard.el.remove(); else if (twCard.previous != null) twCard.el.setAttribute("content", twCard.previous);
            if (twTitle.created) twTitle.el.remove(); else if (twTitle.previous != null) twTitle.el.setAttribute("content", twTitle.previous);
            if (twDesc.created) twDesc.el.remove(); else if (twDesc.previous != null) twDesc.el.setAttribute("content", twDesc.previous);
            if (canon.created) canon.el.remove(); else if (canon.previous != null) canon.el.setAttribute("href", canon.previous);
            ldScripts.forEach((s) => s.remove());
        };
        // `jsonLd` is intentionally omitted: it's an array/object recreated every render,
        // which would cause an infinite re-run loop. `jsonLdKey` acts as its stable identity.
        // The local `desc/ogTitle/ogUrl/canon/...` are all created *inside* this effect;
        // referencing them in the cleanup closure is correct and does not need to be listed.
    }, [title, description, path, jsonLdKey]);
}

// Reusable: build a BreadcrumbList for a page with up to 3 levels
export function breadcrumbSchema(crumbs) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: crumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            item: `${SITE_ORIGIN}${c.path}`,
        })),
    };
}

// Reusable: Service schema
export function serviceSchema({ name, description, path, areaServed = "Medford, OR", serviceType }) {
    return {
        "@context": "https://schema.org",
        "@type": "Service",
        name,
        description,
        serviceType: serviceType || name,
        url: `${SITE_ORIGIN}${path}`,
        provider: {
            "@type": "GeneralContractor",
            name: "5 Star Construction Services LLC",
            telephone: "+1-541-292-9620",
            url: SITE_ORIGIN,
            address: {
                "@type": "PostalAddress",
                addressLocality: "Medford",
                addressRegion: "OR",
                addressCountry: "US",
            },
        },
        areaServed: [
            "Medford, OR", "Ashland, OR", "Central Point, OR", "Eagle Point, OR",
            "Phoenix, OR", "Talent, OR", "White City, OR", "Jacksonville, OR",
            "Grants Pass, OR",
        ],
    };
}
